/**
 * The five answer shapes, as the browser sees them.
 *
 * A hand-written mirror of `server/src/answers.ts`. The two projects do not share
 * a tsconfig — the server is NodeNext and this is a bundler build — so there is
 * no import that would keep them in step automatically. The discriminator is
 * `shape`, and it is the thing to check first when one of these stops lining up:
 * a payload whose `shape` is unknown renders as a failure here rather than as a
 * blank card, which is the only way a mismatch announces itself.
 */

/** Blue, amber, grey. Never green — `07-acceptance.md`, rule 1. */
export type Tone = "ok" | "watch" | "thin";

/**
 * Colour is never the only signal. `tone` chooses the hue *and* the glyph,
 * `word` is the label printed beside it, and `sentence` is the finding itself.
 * All three are rendered, so a greyscale screenshot still says what happened.
 */
export type Verdict = { tone: Tone; word: string; sentence: string };

export type Point = { day: string; value: number | null };

export type TrendAnswer = {
  id: "pump";
  shape: "trend";
  verdict: Verdict;
  entity: string | null;
  metric: string;
  unit: string;
  days: number;
  series: Point[];
  perWeek: number | null;
  recent: number | null;
  baseline: number | null;
};

export type TwoNumbersAnswer = {
  id: "usage";
  shape: "two-numbers";
  verdict: Verdict;
  days: number;
  unit: string;
  current: number | null;
  previous: number | null;
  meters: { entityId: string; current: number; previous: number }[];
};

export type ClockAnswer = {
  id: "tank";
  shape: "clock";
  verdict: Verdict;
  entity: string | null;
  unit: string;
  days: number;
  lowMark: number | null;
  hours: { hour: number; readings: number; low: number }[];
};

export type RankedAnswer = {
  id: "rooms";
  shape: "ranked";
  verdict: Verdict;
  days: number;
  unit: string;
  rooms: { entityId: string; avg: number; min: number | null; max: number | null; days: number }[];
  excluded: string[];
};

export type BeforeAfterAnswer = {
  id: "geyser";
  shape: "before-after";
  verdict: Verdict;
  entity: string | null;
  metric: string;
  unit: string;
  pivot: string | null;
  pivotFrom: string | null;
  window: number;
  before: Point[];
  after: Point[];
  beforeMean: number | null;
  afterMean: number | null;
};

export type Answer = TrendAnswer | TwoNumbersAnswer | ClockAnswer | RankedAnswer | BeforeAfterAnswer;

/**
 * The one number formatter every shape uses.
 *
 * en-ZA, matching `fmt.ts` and matching the identical formatter the server uses
 * inside the verdict sentence. Before both ends were pinned, a card could show
 * "812.4 kWh" in its sentence and "812,4" in the figure directly under it —
 * one character apart, and enough to make a reader check whether they were
 * looking at the same number.
 *
 * `null` is an em dash, never a zero. `07-acceptance.md` rule 3: a figure that
 * can be unwired renders `—`, and inventing a plausible zero for a source that
 * never reported is the same lie as presenting a cached figure as live.
 */
const NUM = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 1 });
export function fig(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? "—" : NUM.format(v);
}

/** The glyph for a tone. Only names that exist in `Icon.svelte`'s map. */
export const TONE_ICON: Record<Tone, string> = {
  ok: "check",
  watch: "alert",
  thin: "pause",
};

/**
 * `switch.borehole_pump` → "Borehole pump".
 *
 * The raw id is what the database calls it and not what a person calls it. It is
 * still shown, one size down, because it is what you need when writing a query
 * or an allowlist entry — it just should not be the thing the eye lands on.
 */
export function nice(entityId: string): string {
  const bare = entityId.replace(/^[a-z_]+\./, "").replace(/_/g, " ");
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}
